import React, { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { Bell, Volume2, Settings2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { unlockAudioContext, playNotificationSound } from '@/lib/audioManager';
import { toast } from 'sonner';
import { useBranding } from '@/contexts/BrandingContext';
import { usePushSubscription } from '@/hooks/usePushSubscription';

const DAILY_REMINDER_KEY = 'sound-permission-last-prompt';
const STATUS_KEY = 'sound-permission-status';

const shouldShowModal = async (): Promise<boolean> => {
  const status = localStorage.getItem(STATUS_KEY);

  // If browser already granted permission, check real push subscription
  if ('Notification' in window && Notification.permission === 'granted') {
    if (status === 'push-registered') return false;

    // Check if active push subscription exists in SW
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration('/');
        if (reg?.active) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            // Push already active, fix localStorage and suppress modal
            localStorage.setItem(STATUS_KEY, 'push-registered');
            localStorage.setItem('notification-sound-enabled', 'true');
            return false;
          }
        }
      }
    } catch {}

    // Permission granted but no push — accept silently, don't nag
    if (!status || status === 'declined') {
      localStorage.setItem(STATUS_KEY, 'accepted');
      localStorage.setItem('notification-sound-enabled', 'true');
      return false;
    }
  }

  if (!status) return true;
  if (status === 'accepted' || status === 'retry-needed') return false;
  if (status === 'push-registered') return false;

  if (status === 'declined') {
    const lastPrompt = localStorage.getItem(DAILY_REMINDER_KEY);
    if (!lastPrompt) return true;
    const elapsed = Date.now() - parseInt(lastPrompt, 10);
    return elapsed > 24 * 60 * 60 * 1000;
  }

  return false;
};

const isBrowserDenied = (): boolean => {
  return 'Notification' in window && Notification.permission === 'denied';
};

export default function SoundPermissionModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const { logoUrl } = useBranding();
  const { subscribe: subscribePush } = usePushSubscription();

  useEffect(() => {
    const timer = setTimeout(async () => {
      // Auto-resubscribe silently if permission granted but no push subscription
      const status = localStorage.getItem(STATUS_KEY);
      if ('Notification' in window && Notification.permission === 'granted') {
        // Check if push subscription actually exists
        let hasPushSub = false;
        try {
          if ('serviceWorker' in navigator) {
            const reg = await navigator.serviceWorker.getRegistration('/');
            if (reg?.active) {
              const sub = await reg.pushManager.getSubscription();
              hasPushSub = !!sub;
            }
          }
        } catch {}

        // If permission granted but no push sub, try to register
        if (!hasPushSub && status !== 'push-registered') {
          logger.log('[SoundPermission] Permission granted but no push sub, auto-subscribing (status:', status, ')');
          const success = await subscribePush();
          if (success) {
            localStorage.setItem(STATUS_KEY, 'push-registered');
            localStorage.setItem('notification-sound-enabled', 'true');
          } else if (!status || status === 'declined') {
            localStorage.setItem(STATUS_KEY, 'retry-needed');
            localStorage.setItem('notification-sound-enabled', 'true');
          }
          // Don't show modal — user already accepted before
          return;
        }

        // If push sub exists, fix status
        if (hasPushSub && status !== 'push-registered') {
          localStorage.setItem(STATUS_KEY, 'push-registered');
          localStorage.setItem('notification-sound-enabled', 'true');
          return;
        }

        // Already registered, skip
        if (status === 'push-registered') return;
      }

      const show = await shouldShowModal();
      if (show) {
        setDenied(isBrowserDenied());
        setIsOpen(true);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [subscribePush]);

  const handleAccept = async () => {
    setLoading(true);

    try {
      await unlockAudioContext();

      let permissionGranted = false;

      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        permissionGranted = permission === 'granted';

        if (permissionGranted) {
          new Notification('ForjaHub', {
            body: 'Notificações ativadas com sucesso!',
            icon: logoUrl || '/favicon.png',
          });
          localStorage.setItem('browser-notifications-enabled', 'true');
        }
      }

      await playNotificationSound();

      // CRITICAL: Call subscribePush IMMEDIATELY in the user gesture context
      // iOS requires push subscription to happen during user interaction
      if (permissionGranted) {
        localStorage.setItem(STATUS_KEY, 'accepted');
        
        // Start push registration in the SAME user gesture
        const pushSuccess = await subscribePush();
        
        if (pushSuccess) {
          localStorage.setItem(STATUS_KEY, 'push-registered');
          toast.success('Notificações push ativadas com sucesso!');
        } else {
          // Mark as retry-needed so background loop picks it up
          localStorage.setItem(STATUS_KEY, 'retry-needed');
          toast.success('Notificações ativadas! Registro push será concluído em breve.');
        }
      } else {
        toast.success('Sons ativados!');
        localStorage.setItem(STATUS_KEY, 'accepted');
      }
    } catch {
      toast.success('Sons ativados!');
      localStorage.setItem(STATUS_KEY, 'accepted');
    } finally {
      localStorage.setItem('notification-sound-enabled', 'true');
      localStorage.setItem(DAILY_REMINDER_KEY, String(Date.now()));
      setLoading(false);
      setIsOpen(false);
    }
  };

  const handleDecline = () => {
    localStorage.setItem(STATUS_KEY, 'declined');
    localStorage.setItem('notification-sound-enabled', 'false');
    localStorage.setItem(DAILY_REMINDER_KEY, String(Date.now()));
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Bell className="w-8 h-8 text-primary animate-bounce" />
          </div>
          <DialogTitle className="text-xl text-center w-full">
            🔔 Ative as notificações — é essencial!
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {denied ? (
              <>
                As notificações estão{' '}
                <strong className="text-foreground">
                  bloqueadas no seu navegador
                </strong>
                . Para receber alertas de negociações e mensagens importantes,
                siga os passos:
              </>
            ) : (
              <>
                As notificações são de{' '}
                <strong className="text-foreground">extrema importância</strong>{' '}
                para você não perder{' '}
                <strong className="text-foreground">
                  mensagens de negociações, atualizações da comunidade, lives e
                  lançamentos de novidades
                </strong>
                . Com elas ativadas, você é avisado no celular ou no computador{' '}
                <strong className="text-foreground">mesmo com o app fechado</strong>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 pt-4">
          {denied ? (
            <>
              <div className="rounded-lg border border-border bg-muted/50 p-3 text-xs text-muted-foreground space-y-2">
                <p className="font-medium text-foreground">Como reativar:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>
                    Toque no{' '}
                    <strong>ícone de cadeado/ajustes</strong> na barra de
                    endereço
                  </li>
                  <li>
                    Encontre <strong>"Notificações"</strong> e altere para{' '}
                    <strong>"Permitir"</strong>
                  </li>
                  <li>Recarregue a página</li>
                </ol>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  localStorage.setItem(STATUS_KEY, 'declined');
                  localStorage.setItem(DAILY_REMINDER_KEY, String(Date.now()));
                  setIsOpen(false);
                }}
                className="gap-2 w-full"
              >
                <Settings2 className="w-4 h-4" />
                Entendi
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleAccept}
                className="gap-2 w-full"
                disabled={loading}
              >
                <Volume2 className="w-4 h-4" />
                {loading ? 'Ativando...' : 'Ativar Notificações'}
              </Button>

              <Button
                variant="ghost"
                onClick={handleDecline}
                className="gap-2 w-full text-muted-foreground"
                disabled={loading}
              >
                Lembrar depois
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
